<template>
  <div>
    <v-card elevation="0" class="pb-2 rounded-lg">
      <div class="pa-4">
        <div class="d-flex justify-space-between align-center mb-2">
          <h5 class="ma-0">Faturas</h5>
          <v-btn
            @click="processInvoices"
            color="primary"
            depressed
            small
            :loading="processing"
          >
            Processar Faturas do Mês
          </v-btn>
        </div>
        <v-alert v-if="error" type="error" class="mb-0">
          {{ error }}
        </v-alert>
        <template v-else>
          <div class="d-flex gap-2 align-center">
            <v-chip class="pa-0">
              <div>
                <v-btn icon @click="previousReference">
                  <v-icon>mdi-chevron-left</v-icon>
                </v-btn>
                {{ reference }}
                <v-btn icon @click="nextReference" :disabled="hasNextMonth">
                  <v-icon>mdi-chevron-right</v-icon>
                </v-btn>
              </div>
            </v-chip>

            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              label="Pesquisar"
              single-line
              dense
              hide-details
              outlined
            />
            <v-btn @click="getInvoices" icon small>
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </div>

          <v-data-table
            :loading="loading"
            :items="invoices"
            :headers="headers"
            :search="search"
            class="elevation-0"
          >
            <template v-slot:item.Company.name="{ item }">
              <base-avatar
                :seed="item.Company.id"
                :size="20"
                avatarStyle="glass"
                color="secondary lighten-2"
              />
              <span class="ml-2">{{ item.Company.name }}</span>
            </template>
            <template v-slot:item.reference="{ item }">
              {{ item.month }}/{{ item.year }}
            </template>
            <template v-slot:item.dueDate="{ item }">
              {{ item.dueDate | date }}
            </template>
            <template v-slot:item.amount="{ item }">
              {{ item.amount | currency }}
            </template>
            <template v-slot:item.paidAmount="{ item }">
              <span v-if="item.status === 'PAID'">
                {{ item.paidAmount | currency }}
              </span>
              <span v-else class="text--secondary">—</span>
            </template>
            <template v-slot:item.cashStatus="{ item }">
              <v-chip
                v-if="item.CashEntry"
                :color="getCashEntryColor(item.CashEntry)"
                label
                small
                :to="getCashEntryLink(item.CashEntry)"
              >
                {{ getCashEntryLabel(item.CashEntry) }}
              </v-chip>
              <span v-else-if="item.status === 'CLOSED'" class="text--secondary">
                Sem lançamento
              </span>
              <span v-else class="text--secondary">—</span>
            </template>
            <template v-slot:item.status="{ item }">
              <v-chip
                :color="INVOICE_DEFINITIONS.status[item.status].color"
                label
                small
              >
                {{ INVOICE_DEFINITIONS.status[item.status].label }}
              </v-chip>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-icon small class="mr-2" @click="invoiceDetails(item)">
                mdi-eye
              </v-icon>
            </template>
          </v-data-table>
        </template>
      </div>
    </v-card>
    <InvoiceDetails />
  </div>
</template>
<script>
import { mapGetters, mapActions } from "vuex";
import moment from "moment";
import INVOICE from "@/services/ultimate/invoices.js";
import INVOICE_DEFINITIONS from "@/definitions/InvoiceDefinitions";
import CASH_DEFINITIONS from "@/definitions/CashDefinitions";
import InvoiceDetails from "@/components/ultimate/invoice/InvoiceDetails.vue";

export default {
  components: {
    InvoiceDetails,
  },
  metaInfo: {
    title: "Faturas",
  },
  data() {
    return {
      error: null,
      loading: false,
      processing: false,
      invoices: [],
      search: "",
      reference: moment().format("MM/YYYY"),
      INVOICE_DEFINITIONS,
      CASH_DEFINITIONS,
      headers: [
        // { text: "Referência", value: "reference" },
        { text: "Empresa", value: "Company.name" },
        { text: "Valor", value: "amount" },
        { text: "Recebido", value: "paidAmount" },
        { text: "Data de vencimento", value: "dueDate" },
        { text: "Status", value: "status" },
        { text: "Caixa", value: "cashStatus", sortable: false },
        { text: "Ações", value: "actions", sortable: false },
      ],
    };
  },
  methods: {
    ...mapActions("auth", ["getPermissions"]),
    async getInvoices() {
      try {
        this.loading = true;
        var response = await INVOICE.getAll({ reference: this.reference });
        this.invoices = response.invoices;
      } catch (error) {
        this.error = error.message || "Erro ao buscar changelogs";
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
    async processInvoices() {
      try {
        this.processing = true;
        const date = moment(this.reference, "MM/YYYY");
        await INVOICE.processInvoices({
          month: date.month() + 1,
          year: date.year(),
        });
        await this.getInvoices();
        alert("Faturas processadas com sucesso");
      } catch (error) {
        alert(error.message || "Erro ao processar faturas");
      } finally {
        this.processing = false;
      }
    },
    previousReference() {
      this.reference = moment(this.reference, "MM/YYYY")
        .subtract(1, "month")
        .format("MM/YYYY");
      this.getInvoices();
    },
    nextReference() {
      this.reference = moment(this.reference, "MM/YYYY")
        .add(1, "month")
        .format("MM/YYYY");
      this.getInvoices();
    },
    invoiceDetails(invoice) {
      this.$emit("invoice-details", invoice);
    },
    getCashEntryLabel(cashEntry) {
      return CASH_DEFINITIONS.status[cashEntry.status]?.label || cashEntry.status;
    },
    getCashEntryColor(cashEntry) {
      return CASH_DEFINITIONS.status[cashEntry.status]?.color || "grey";
    },
    getCashEntryLink(cashEntry) {
      return {
        name: "ultimate.cash-control",
        query: { entryId: cashEntry.id },
      };
    },
  },

  computed: {
    ...mapGetters("company", ["selectedCompany"]),
    numberOfPages() {
      return Math.ceil(this.invoices.length / this.itemsPerPage);
    },
    hasNextMonth() {
      return moment(this.reference, "MM/YYYY").isSame(
        moment().startOf("month"),
        "month"
      );
    },
  },
  watch: {},
  async mounted() {
    await this.getInvoices();
  },
};
</script>
